import * as React from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import { Delete as DeleteIcon } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import Button from "@mui/material/Button";
import "../App.css";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import axios from "axios";
import { useAcademicYear } from "./AcademicYearContext";

const ModalStyle = {
  position: "absolute",
  borderRadius: "5px",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 300,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 2,
  pb: 1,
  pr: 1,
  color: 'black'
};

export default function EventReturnProduct({
  SelectedEventName,
  SelectedEventCategory,
  staffId1,
  staffName,
  selectedIssueProductId,

  roleName,
  setopenReturnProductComponent,
  EventProduct,
  setOpenComponentRefundSale,
  getProduct
}) {

  // Stable data state - completely isolated from parent
  const [stableData, setStableData] = useState([]);
  const [isDataLocked, setIsDataLocked] = useState(false);

  // UI state
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("event_issue_product_details_id");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [markedForDelete, setMarkedForDelete] = useState([]);
  const [selectedQuantity, setSelectedQuantity] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [disabledRows, setDisabledRows] = useState([]);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  // System state
  const [userId, setUserId] = useState();
  const [statusIds, setStatusIds] = useState({
    issued: null,
    created: null,
    refund: null,
    partialRefund: null
  });

  const { academicYearId } = useAcademicYear();

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Refs for component lifecycle
  const mountedRef = useRef(true);
  const dataInitialized = useRef(false);
  const operationInProgress = useRef(false);

  // Lock data to prevent parent interference
  useEffect(() => {
    if (Array.isArray(EventProduct) && EventProduct.length > 0 && !dataInitialized.current) {
      const filtered = EventProduct.filter(
        (item) => item.event_issue_product_status_id === 2 || item.event_issue_product_status_id === 3
      );
      setStableData([...filtered]); // Create deep copy
      setIsDataLocked(true);
      dataInitialized.current = true;
      console.log("Data locked and initialized:", filtered);
    }
  }, [EventProduct]);

  // Prevent any external updates once data is locked
  useEffect(() => {
    if (isDataLocked && !operationInProgress.current) {
      // Ignore any external changes to EventProduct
      return;
    }
  }, [EventProduct, isDataLocked]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Initialize user data
  useEffect(() => {
    let isMounted = true;

    const fetchUserData = async () => {
      const email = localStorage.getItem("user_email");
      if (!email) return;

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_JHS_DEVELOPMENT}/profile/profiledata`,
          { email },
          { headers: { user_email: email } }
        );

        if (response.status === 200 && isMounted) {
          setUserId(response.data.user.user_id);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();

    return () => {
      isMounted = false;
    };
  }, []);

  // Initialize status data
  useEffect(() => {
    let isMounted = true;

    const getSaleStatus = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_JHS_DEVELOPMENT}/createProduct/getSalesStatus`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();

        if (isMounted) {
          setStatusIds({
            created: data.find((item) => item.description === "Created")?.issue_product_status_id || null,
            issued: data.find((item) => item.description === "Issued")?.issue_product_status_id || null,
            partialRefund: data.find((item) => item.description === "Partial Return")?.issue_product_status_id || null,
            refund: data.find((item) => item.description === "Returned")?.issue_product_status_id || null
          });
        }
      } catch (error) {
        console.error("Error fetching sales status:", error);
      }
    };

    getSaleStatus();

    return () => {
      isMounted = false;
    };
  }, []);

  // Core delete operation with full isolation
  const performReturnOperation = useCallback(async (itemsToDelete) => {
    if (operationInProgress.current || !itemsToDelete.length || !userId) return;

    operationInProgress.current = true;
    setIsProcessing(true);

    // Create optimistic update immediately
    const optimisticData = stableData.reduce((acc, row) => {
      const deleteItem = itemsToDelete.find(
        (item) => item.issueProductDetailsId === row.event_issue_product_details_id
      );

      if (!deleteItem) {
        acc.push(row);
        return acc;
      }

      const qtyToReturn = deleteItem.quantitySelected ?? deleteItem.quantity ?? 0;

      if (qtyToReturn < row.quantity) {
        // Partial return - update quantity
        acc.push({
          ...row,
          quantity: row.quantity - qtyToReturn,
        });
      }
      // Full return - don't add to acc (removes item)

      return acc;
    }, []);

    // Apply optimistic update
    setStableData(optimisticData);

    // Prepare API payload
    const payload = itemsToDelete.map((item) => ({
      event_issue_product_details_id: item.issueProductDetailsId,
      product_id: item.productId,
      event_issue_product_id: selectedIssueProductId,
      issue_product_status: statusIds.partialRefund,
      issue_product_status_id: statusIds.refund,
      quantitySelected: item.quantitySelected ?? item.quantity ?? 0,
      return_date: new Date(),
      updated_by: userId,
      updated_timestamp: new Date(),
    }));

    try {
      const response = await fetch(
        `${process.env.REACT_APP_JHS_DEVELOPMENT}/events/deleteInventoryRefund`,
        {
          method: "POST",
          headers: { "Content-type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ refunds: payload }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to return products');
      }

      if (mountedRef.current) {
        // Success - reset form state
        setMarkedForDelete([]);
        setDisabledRows([]);
        setSelectedQuantity({});
        setValidationErrors({});

        setSnackbar({
          open: true,
          message: "Product(s) returned successfully.",
          severity: "success",
        });

        // Silent background update to parent (no UI impact)
        setTimeout(() => {
          if (getProduct && typeof getProduct === 'function' && mountedRef.current) {
            getProduct(selectedIssueProductId).catch(console.error);
          }
        }, 500);
      }

    } catch (error) {
      console.error("❌ Error in return operation:", error);

      // Revert optimistic update on error
      if (mountedRef.current) {
        // Restore original data
        const originalFiltered = EventProduct.filter(
          (item) => item.event_issue_product_status_id === 2 || item.event_issue_product_status_id === 3
        );
        setStableData([...originalFiltered]);

        setSnackbar({
          open: true,
          message: "Failed to return products. Please try again.",
          severity: "error",
        });
      }
    } finally {
      if (mountedRef.current) {
        setIsProcessing(false);
      }
      operationInProgress.current = false;
    }
  }, [stableData, selectedIssueProductId, statusIds, userId, getProduct, EventProduct]);

  // Item management handlers
  const handleAddToReturn = useCallback((issueProductDetailsId, product_id, quantity, event_issue_product_id) => {
    const selectedQty = selectedQuantity[issueProductDetailsId] ?? quantity;

    if (selectedQty < 1 || selectedQty > quantity) {
      console.error(`Error: Quantity must be between 1 and ${quantity}.`);
      return;
    }

    const isAlreadyMarked = markedForDelete.some(
      (item) => item.issueProductDetailsId === issueProductDetailsId
    );

    if (!isAlreadyMarked) {
      setSelectedQuantity((prev) => ({
        ...prev,
        [issueProductDetailsId]: selectedQty,
      }));

      setMarkedForDelete((prev) => [
        ...prev,
        {
          issueProductDetailsId,
          productId: product_id,
          quantity,
          issue_product_id: event_issue_product_id,
          quantitySelected: selectedQty,
        },
      ]);

      setDisabledRows((prev) => [...prev, issueProductDetailsId]);
    }
  }, [selectedQuantity, markedForDelete]);

  const handleRemoveFromReturn = useCallback((issueProductDetailsIdToRemove) => {
    setMarkedForDelete((prev) =>
      prev.filter((item) => item.issueProductDetailsId !== issueProductDetailsIdToRemove)
    );

    setSelectedQuantity((prev) => {
      const updated = { ...prev };
      delete updated[issueProductDetailsIdToRemove];
      return updated;
    });

    setDisabledRows((prev) =>
      prev.filter((id) => id !== issueProductDetailsIdToRemove)
    );
  }, []);

  const handleToggleReturn = useCallback((row) => {
    const isMarked = markedForDelete.some(
      (item) => item.issueProductDetailsId === row.event_issue_product_details_id
    );

    if (isMarked) {
      handleRemoveFromReturn(row.event_issue_product_details_id);
    } else {
      handleAddToReturn(
        row.event_issue_product_details_id,
        row.product_id,
        row.quantity,
        row.event_issue_product_id
      );
    }
  }, [markedForDelete, handleAddToReturn, handleRemoveFromReturn]);

  const handleQuantityUpdate = useCallback((issueProductDetailsId, value, maxQuantity) => {
    const numericValue = parseInt(value, 10);

    if (value === "" || isNaN(numericValue)) {
      setValidationErrors((prev) => ({
        ...prev,
        [issueProductDetailsId]: `Quantity is required`,
      }));
      setSelectedQuantity((prev) => ({
        ...prev,
        [issueProductDetailsId]: "",
      }));
    } else if (numericValue > maxQuantity) {
      setValidationErrors((prev) => ({
        ...prev,
        [issueProductDetailsId]: `Quantity cannot exceed ${maxQuantity}`,
      }));
    } else if (numericValue < 1) {
      setValidationErrors((prev) => ({
        ...prev,
        [issueProductDetailsId]: ``,
      }));
    } else {
      setValidationErrors((prev) => ({
        ...prev,
        [issueProductDetailsId]: null,
      }));
      setSelectedQuantity((prev) => ({
        ...prev,
        [issueProductDetailsId]: numericValue,
      }));
    }
  }, []);

  // Modal and action handlers
  const resetSelection = useCallback(() => {
    setIsConfirmationOpen(false);
    setMarkedForDelete([]);
    setDisabledRows([]);
    setSelectedQuantity({});
  }, []);

  const showNoSelectionDialog = useCallback(() => {
    setDeleteMsg("Please select product(s) before proceeding with the return.");
    setIsConfirmationOpen(true);
  }, []);

  const showConfirmDialog = useCallback(() => {
    setDeleteMsg("Are you sure you want to return the selected products?");
    setIsConfirmationOpen(true);
  }, []);

  const handleReturnClick = useCallback(() => {
    if (markedForDelete.length > 0) {
      showConfirmDialog();
    } else {
      showNoSelectionDialog();
    }
    setopenReturnProductComponent(false);
  }, [markedForDelete.length, showConfirmDialog, showNoSelectionDialog, setopenReturnProductComponent]);

  const confirmReturn = useCallback(async () => {
    setIsConfirmationOpen(false);
    await performReturnOperation(markedForDelete);
  }, [performReturnOperation, markedForDelete]);

  // Table handlers
  const handlePageChange = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleSortRequest = useCallback((property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  }, [order, orderBy]);

  // Sorting logic
  const comparator = useCallback((a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  }, []);

  const sortComparator = useCallback((order, orderBy) => {
    return order === "desc"
      ? (a, b) => comparator(a, b, orderBy)
      : (a, b) => -comparator(a, b, orderBy);
  }, [comparator]);

  // Memoized processed data
  const processedData = useMemo(() => {
    if (!stableData.length) return [];

    return stableData
      .map((row, index) => ({ ...row, index: index + 1 }))
      .sort(sortComparator(order, orderBy));
  }, [stableData, sortComparator, order, orderBy]);

  const currentPageData = useMemo(() => {
    if (!processedData.length) return [];

    return rowsPerPage > 0
      ? processedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : processedData;
  }, [processedData, page, rowsPerPage]);



  return (
    <div style={{ backgroundColor: 'white', minHeight: '100%' }}>
      <div
        style={{
          margin: "10px 0",
          fontSize: 15,
          fontWeight: "bold",
          color: "#0A64A2",
          paddingTop: "0",
        }}
      >
        Indent ID: {selectedIssueProductId}
      </div>

      <div className="main_title1">
        <div>
          Event Name: {SelectedEventName} &nbsp;&nbsp; Event Category: {SelectedEventCategory}
        </div>
      </div>

      <TableContainer component={Paper} className='return_sale_table_container'>
        <Table sx={{ minWidth: 700 }} aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell align='center' className="table-cell">
                <TableSortLabel
                  className="table-sort-label"
                  active={orderBy === "event_issue_product_details_id"}
                  direction={orderBy === "event_issue_product_details_id" ? order : "desc"}
                  onClick={() => handleSortRequest("event_issue_product_details_id")}
                >
                  SL No.
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="center" className="table-cell">
                <TableSortLabel
                  className="table-sort-label"
                  active={orderBy === "product_name"}
                  direction={orderBy === "product_name" ? order : "desc"}
                  onClick={() => handleSortRequest("product_name")}
                >
                  Product
                  {orderBy === "product_name" ? (
                    <span style={visuallyHidden}>
                      {order === "desc" ? "sorted descending" : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="center" className="table-cell">
                <TableSortLabel
                  className="table-sort-label"
                  active={orderBy === "issue_date"}
                  direction={orderBy === "issue_date" ? order : "desc"}
                  onClick={() => handleSortRequest("issue_date")}
                >
                  Issue Date
                  {orderBy === "issue_date" ? (
                    <span style={visuallyHidden}>
                      {order === "desc" ? "sorted descending" : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="center" className="table-cell">
                <TableSortLabel
                  className="table-sort-label"
                  active={orderBy === "attribute_value"}
                  direction={orderBy === "attribute_value" ? order : "desc"}
                  onClick={() => handleSortRequest("attribute_value")}
                >
                  Attribute
                  {orderBy === "attribute_value" ? (
                    <span style={visuallyHidden}>
                      {order === "desc" ? "sorted descending" : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="center" className="table-cell">
                <TableSortLabel
                  className="table-sort-label"
                  active={orderBy === "attribute_name"}
                  direction={orderBy === "attribute_name" ? order : "desc"}
                  onClick={() => handleSortRequest("attribute_name")}
                >
                  Attribute Value
                  {orderBy === "attribute_name" ? (
                    <span style={visuallyHidden}>
                      {order === "desc" ? "sorted descending" : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="center" className="table-cell">
                <TableSortLabel
                  className="table-sort-label"
                  active={orderBy === "quantity"}
                  direction={orderBy === "quantity" ? order : "desc"}
                  onClick={() => handleSortRequest("quantity")}
                >
                  Quantity
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="center"></StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentPageData.map((row) => {
              const isMarkedForReturn = markedForDelete.some(
                (item) => item.issueProductDetailsId === row.event_issue_product_details_id
              );

              return (
                <StyledTableRow key={`${row.event_issue_product_details_id}-${row.quantity}`}>
                  <StyledTableCell align='center' component="th" scope="row" className='table-cell-border'>
                    {row.event_issue_product_details_id}
                    {isMarkedForReturn && <div className='centered-red-line' />}
                  </StyledTableCell>
                  <StyledTableCell align="center" className='table-cell-border'>
                    {row.product_name}
                    {isMarkedForReturn && <div className='centered-red-line' />}
                  </StyledTableCell>
                  <StyledTableCell align="center" className='table-cell-border'>
                    {row.issue_date}
                    {isMarkedForReturn && <div className='centered-red-line' />}
                  </StyledTableCell>
                  <StyledTableCell align="center" className='table-cell-border'>
                    {row.attribute_value}
                    {isMarkedForReturn && <div className='centered-red-line' />}
                  </StyledTableCell>
                  <StyledTableCell align="center" className='table-cell-border'>
                    {row.attribute_name}
                    {isMarkedForReturn && <div className='centered-red-line' />}
                  </StyledTableCell>
                  <StyledTableCell align="center" className="table-cell-border">
                    {isMarkedForReturn && <div className="centered-red-line" />}
                    {row.quantity > 1 ? (
                      <div>
                        <input
                          disabled={disabledRows.includes(row.event_issue_product_details_id) || isProcessing}
                          type="number"
                          min="1"
                          max={row.quantity}
                          value={
                            selectedQuantity[row.event_issue_product_details_id] !== undefined
                              ? selectedQuantity[row.event_issue_product_details_id]
                              : row.quantity
                          }
                          onChange={(e) => {
                            handleQuantityUpdate(
                              row.event_issue_product_details_id,
                              e.target.value,
                              row.quantity
                            );
                          }}
                          style={{ width: "60px", textAlign: "center" }}
                        />
                        {validationErrors[row.event_issue_product_details_id] && (
                          <div style={{ color: "red", fontSize: "12px" }}>
                            {validationErrors[row.event_issue_product_details_id]}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span>{row.quantity}</span>
                    )}
                  </StyledTableCell>
                  <StyledTableCell align="center" style={{ lineHeight: "10px", padding: "5px" }}>
                    <IconButton
                      color="black"
                      onClick={() => handleToggleReturn(row)}
                      disabled={isProcessing}
                      title={isMarkedForReturn ? "Remove from return" : "Add to return"}
                    >
                      <DeleteIcon color="action" />

                    </IconButton>
                  </StyledTableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>

          <TableFooter>
            <TableRow>
              <TablePagination
                sx={{
                  backgroundColor: "#0A64A2",
                  color: "white",
                  width: "100%",
                }}
                rowsPerPageOptions={[5, 10, 25]}
                colSpan={7}
                count={stableData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: '10px',
        gap: '10px',
        backgroundColor: 'white',
        padding: '10px'
      }}>
        <Button
          className="save_update_btn"
          variant="contained"
          onClick={handleReturnClick}
          disabled={isProcessing}
          sx={{
            opacity: isProcessing ? 0.6 : 1,
            transition: 'opacity 0.2s ease'
          }}
        >
          {isProcessing ? 'Processing Return...' : 'Return'}

        </Button>
      </div>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Confirmation Modal */}
      <Modal
        open={isConfirmationOpen}
        onClose={resetSelection}
        closeAfterTransition
      >
        <Box sx={ModalStyle}>
          <Typography sx={{ fontSize: '1.1em', color: '#0A64A2', marginBottom: 2 }}>
            {deleteMsg}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            {markedForDelete.length > 0 && (
              <>
                <Button
                  variant="contained"
                  onClick={confirmReturn}
                  className="no-btn"
                  disabled={isProcessing}
                  color="primary"
                >
                  Yes
                </Button>
                <Button
                  variant="contained"
                  onClick={resetSelection}
                  className="no-btn"
                  color="primary"
                >
                  No
                </Button>
              </>
            )}
            {!markedForDelete.length && (
              <Button
                variant="contained"
                onClick={resetSelection}
                className="no-btn"
                color="primary"
              >
                OK
              </Button>
            )}
          </Box>
        </Box>
      </Modal>
    </div>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#0A64A2',
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));